import { useSpring, animated } from "@react-spring/web";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Card,
  CardBody,
  Button,
  useColorModeValue,
  Stack,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import {
  EmailIcon,
  ViewIcon,
  CheckCircleIcon,
  ChatIcon,
} from "@chakra-ui/icons";
import { Link } from "react-router-dom";
import MyForm from "../../components/form/MyForm";

const Contact = () => {
  // Colori per dark mode
  const bgColor = useColorModeValue("gray.900", "gray.800");
  const cardBg = useColorModeValue("gray.800", "gray.700");
  const textColor = useColorModeValue("gray.100", "gray.200");
  const secondaryTextColor = useColorModeValue("gray.300", "gray.400");
  const borderColor = useColorModeValue("gray.700", "gray.600");
  const accentColor = useColorModeValue("orange.400", "orange.300");

  const springs = useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    config: { tension: 280, friction: 60 },
  });

  return (
    <animated.div style={{ ...springs }}>
      <Helmet>
        <title>Controluce Illuminazione di Monti Daniele - Contatti</title>
        <meta
          name="description"
          content="Non esitate a contattarci per maggiori informazioni sui nostri servizi. Scopri Controluce Illuminazione, service luci, chiedendo un preventivo gratuito e su misura per il tuo evento."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Controluce Illuminazione di Monti Daniele",
            image: [
              "https://www.controluceilluminazione.it/static/media/controluceLogoRWhite.e12538b1615ec247c4cc.png",
              "https://controluceilluminazione.it/static/media/controluceLogoLWhite.e92eabff7d445a556cd2.png",
            ],
            telephone: "+39 055 0935053",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Firenze",
              postalCode: "50018",
              streetAddress: "Via di casellina",
            },
            url: "https://www.controluceilluminazione.it",
            openingHours: "Mo-Fr 09:00-18:00",
            geo: {
              "@type": "GeoCoordinates",
              latitude: "43.7583291",
              longitude: "11.1626199",
            },
            sameAs: [
              "https://www.facebook.com/p/Controluce-Illuminazione-100043546856066",
              "http://instagram.com/controluce_illuminazione/#",
            ],
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <Box py={{ base: "2em", lg: "3em" }}>
        <Container maxW="container.xl" px={{ base: 4, md: 8, lg: 12 }}>
          <VStack spacing={6} align="center" textAlign="start">
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              color={textColor}
              lineHeight="1.2"
            >
              Contattaci per un preventivo gratuito del nostro service luci
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={secondaryTextColor}
              maxW="800px"
              lineHeight="1.8"
            >
              Non esitare a contattarci per maggiori informazioni o per un
              preventivo gratuito, ti risponderemo il prima possibile!
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container
        maxW="container.xl"
        px={{ base: 4, md: 8, lg: 12 }}
        pb={{ base: 8, lg: 12 }}
        pt={0}
      >
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, lg: 12 }}>
          {/* Contact Information */}
          <VStack spacing={8} align="stretch">
            {/* Location Card */}
            <Card
              bg={cardBg}
              borderRadius="2xl"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="dark-lg"
              _hover={{ transform: "translateY(-4px)" }}
              transition="all 0.3s"
            >
              <CardBody p={{ base: 6, lg: 8 }}>
                <VStack spacing={6} align="start">
                  <Heading as="h2" size="lg" color={textColor}>
                    Dove Siamo
                  </Heading>

                  <HStack spacing={4} align="start">
                    <Box
                      w="12"
                      h="12"
                      bg="rgba(255, 140, 0, 0.2)"
                      borderRadius="lg"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      border="1px solid"
                      borderColor={accentColor}
                    >
                      <ViewIcon color={accentColor} boxSize={5} />
                    </Box>
                    <VStack align="start" spacing={2}>
                      <Text color={secondaryTextColor} fontSize="sm">
                        Sede Legale
                      </Text>
                      <Link
                        to="https://www.google.it/maps/place/Controluce+Illuminazione/@43.7583291,11.1626199,18z/data=!4m9!1m2!2m1!1svia+di+casellina,+snc+Isola+E+Interno+Piano+Terra+scandicci+firenze!3m5!1s0x132a57fa612271ed:0x2a2fe1ce1134cf45!8m2!3d43.7584444!4d11.1627486!16s%2Fg%2F1hc6xpxd3?entry=ttu&g_ep=EgoyMDI0MTIxMS4wIKXMDSoASAFQAw%3D%3D"
                        target="_blank"
                      >
                        <Text
                          color={textColor}
                          fontSize="md"
                          fontWeight="medium"
                          _hover={{ color: accentColor }}
                        >
                          Via di casellina snc, 50018 Scandicci (FI)
                        </Text>
                      </Link>
                      <Text color={secondaryTextColor} fontSize="sm">
                        Isola e interno piano terra, stradario 06680, Italia
                      </Text>
                    </VStack>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Why Choose Us */}
            <Card
              bg={cardBg}
              borderRadius="2xl"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="dark-lg"
              _hover={{ transform: "translateY(-4px)" }}
              transition="all 0.3s"
            >
              <CardBody p={{ base: 6, lg: 8 }}>
                <VStack spacing={4} align="start">
                  <Heading as="h3" size="md" color={textColor}>
                    Perché Sceglierci
                  </Heading>
                  <VStack spacing={3} align="start">
                    <HStack spacing={3}>
                      <CheckCircleIcon color={accentColor} />
                      <Text mb={0} color={secondaryTextColor}>
                        Preventivo gratuito e personalizzato
                      </Text>
                    </HStack>
                    <HStack spacing={3}>
                      <CheckCircleIcon color={accentColor} />
                      <Text mb={0} color={secondaryTextColor}>
                        Esperienza trentennale nel settore
                      </Text>
                    </HStack>

                    <HStack spacing={3}>
                      <CheckCircleIcon color={accentColor} />
                      <Text mb={0} color={secondaryTextColor}>
                        Attrezzature professionali e moderne
                      </Text>
                    </HStack>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </VStack>

          {/* Contact Form */}
          <Card
            bg={cardBg}
            id="contactForm"
            borderRadius="2xl"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="dark-lg"
            _hover={{ transform: "translateY(-4px)" }}
            transition="all 0.3s"
          >
            <CardBody p={{ base: 6, lg: 8 }}>
              <VStack spacing={6} align="start">
                <Heading as="h2" size="lg" color={textColor}>
                  Richiedi un Preventivo
                </Heading>
                <Text color={secondaryTextColor}>
                  Compila il form sottostante per richiedere un preventivo
                  gratuito e personalizzato per il tuo evento.
                </Text>

                <MyForm />
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>
      </Container>

      {/* Map Section */}
      <Box py={{ base: 8, lg: 12 }} bg={bgColor}>
        <Container maxW="container.xl" px={{ base: 4, md: 8, lg: 12 }}>
          <VStack spacing={6} align="center">
            <Heading as="h2" size="xl" color={textColor} textAlign="center">
              Trova la Nostra Sede
            </Heading>
            <Box
              width="100%"
              height={{ base: "400px", lg: "500px" }}
              borderRadius="2xl"
              overflow="hidden"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="dark-lg"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2880.156412732299!2d11.16057377649398!3d43.75844447109947!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132a57fa612271ed%3A0x2a2fe1ce1134cf45!2sControluce%20Illuminazione!5e0!3m2!1sit!2sit!4v1701700000000!5m2!1sit!2sit"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mappa sede Controluce Illuminazione"
              ></iframe>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container
        maxW="container.xl"
        px={{ base: 4, md: 8, lg: 12 }}
        py={{ base: 8, lg: 12 }}
      >
        <Box
          bg="linear-gradient(135deg, rgba(255, 179, 0, 0.4) 0%, rgba(255, 143, 0, 0.3) 100%)"
          border="1px solid"
          borderColor="orange.200"
          borderRadius="2xl"
          p={{ base: 8, lg: 12 }}
          textAlign="center"
        >
          <VStack spacing={6}>
            <Heading as="h2" size="xl" color={textColor}>
              Pronto a Illuminare il Tuo Evento?
            </Heading>
            <Text fontSize="lg" color={secondaryTextColor} maxW="600px">
              Contattaci oggi stesso per una consulenza gratuita e scopri come
              possiamo trasformare la tua idea in realtà.
            </Text>
            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={4}
              justify="center"
            >
              <Button
                as="a"
                href="#contactForm"
                colorScheme="orange"
                variant="outline"
                size="lg"
                leftIcon={<EmailIcon />}
                bg="linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "dark-lg",
                }}
                transition="all 0.3s"
              >
                Invia Email
              </Button>
              <Button
                as={Link}
                to="/gallery/event#workGallery"
                colorScheme="orange"
                variant="ghost"
                size="lg"
                leftIcon={<ChatIcon />}
                color={accentColor}
                _hover={{
                  bg: "rgba(255, 140, 0, 0.05)",
                  transform: "translateY(-2px)",
                }}
                transition="all 0.3s"
              >
                Vedi i Nostri Lavori
              </Button>
            </Stack>
          </VStack>
        </Box>
      </Container>
    </animated.div>
  );
};

export default Contact;
